import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../auth';
import { useLanguage } from '../i18n';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '../components/ui/dialog';
import { Plus, Building2, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const CLIENT_TYPES = ['store', 'restaurant', 'cafe', 'chain', 'coupang', 'melnitsa', 'individual', 'other'];
const CLIENT_TYPE_LABELS = {
  store: '🏪 Магазин',
  restaurant: '🍴 Ресторан',
  cafe: '☕ Кафе',
  chain: '🔗 Сеть',
  coupang: '📦 Coupang',
  melnitsa: '🌾 Мельница',
  individual: '👤 Индивидуальный',
  other: '🏢 Другое',
};
const DELIVERY_METHODS = ['free_delivery', 'paid_delivery', 'pickup'];
const DELIVERY_LABELS = {
  free_delivery: '🆓 Бесплатная',
  paid_delivery: '💰 Платная (₩3000/коробка)',
  pickup: '🚗 Самовывоз',
};
const PRICING_GROUPS = ['standard', 'chain', 'individual'];
const LANGUAGES = ['ko', 'ru', 'en', 'uz'];

export default function CompaniesPage() {
  const { t } = useLanguage();
  const [companies, setCompanies] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [locationDialog, setLocationDialog] = useState(null);
  const [locations, setLocations] = useState({});

  const [form, setForm] = useState({
    company_name: '',
    client_type: 'cafe',
    contact_person: '',
    phone: '',
    email: '',
    preferred_language: 'ko',
    pricing_group: 'standard',
    discount_percent: 0,
    payment_terms: 0,
    delivery_method: 'paid_delivery',
    notes: '',
  });

  const [locForm, setLocForm] = useState({
    location_name: '',
    recipient_name: '',
    recipient_phone: '',
    address: '',
    delivery_notes: '',
    default_delivery_method: 'paid_delivery',
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    const res = await axios.get(`${API}/companies`);
    setCompanies(res.data);
    // Fetch locations for each
    for (const c of res.data) {
      const locs = await axios.get(`${API}/delivery-locations/company/${c.id}`);
      setLocations(prev => ({ ...prev, [c.id]: locs.data }));
    }
  };

  const handleCreate = async () => {
    try {
      // Auto-set defaults for Мельница
      const payload = {...form};
      if (payload.client_type === 'melnitsa') {
        payload.discount_percent = payload.discount_percent || 20;
        payload.delivery_method = 'free_delivery';
      }
      await axios.post(`${API}/companies`, payload);
      toast.success('Компания создана');
      setDialogOpen(false);
      setForm({
        company_name: '', client_type: 'cafe', contact_person: '', phone: '', email: '',
        preferred_language: 'ko', pricing_group: 'standard', discount_percent: 0, payment_terms: 0,
        delivery_method: 'paid_delivery', notes: '',
      });
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  const handleCreateLocation = async () => {
    try {
      await axios.post(`${API}/delivery-locations`, {
        ...locForm,
        company_id: locationDialog.id,
      });
      toast.success('Location added');
      setLocationDialog(null);
      setLocForm({
        location_name: '', recipient_name: '', recipient_phone: '',
        address: '', delivery_notes: '', default_delivery_method: 'paid_delivery',
      });
      fetchCompanies();
    } catch (err) {
      toast.error('Failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('companies')}</h1>
          <p className="text-gray-500 mt-1">{companies.length} companies</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="cff-button-primary" data-testid="add-company-btn">
              <Plus className="h-4 w-4 mr-2" /> {t('add')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('add')} {t('company')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label>{t('name')}</Label>
                <Input value={form.company_name} onChange={(e) => setForm({...form, company_name: e.target.value})} data-testid="company-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('clientType')}</Label>
                  <Select value={form.client_type} onValueChange={(v) => setForm({...form, client_type: v, discount_percent: v === 'melnitsa' ? 20 : form.discount_percent, delivery_method: v === 'melnitsa' ? 'free_delivery' : form.delivery_method})}>
                    <SelectTrigger data-testid="client-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CLIENT_TYPES.map(ct => <SelectItem key={ct} value={ct}>{CLIENT_TYPE_LABELS[ct]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Скидка %</Label>
                  <Input
                    type="number"
                    value={form.discount_percent}
                    onChange={(e) => setForm({...form, discount_percent: parseFloat(e.target.value) || 0})}
                    data-testid="discount-input"
                  />
                </div>
              </div>
              <div>
                <Label>{t('contactPerson')}</Label>
                <Input value={form.contact_person} onChange={(e) => setForm({...form, contact_person: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('phone')}</Label>
                  <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
                </div>
                <div>
                  <Label>{t('email')}</Label>
                  <Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('selectLanguage')}</Label>
                  <Select value={form.preferred_language} onValueChange={(v) => setForm({...form, preferred_language: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{LANGUAGES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('deliveryMethod')}</Label>
                  <Select value={form.delivery_method} onValueChange={(v) => setForm({...form, delivery_method: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{DELIVERY_METHODS.map(d => <SelectItem key={d} value={d}>{DELIVERY_LABELS[d]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>{t('paymentTerms')} (days)</Label>
                <Input type="number" value={form.payment_terms} onChange={(e) => setForm({...form, payment_terms: parseInt(e.target.value) || 0})} />
              </div>
              <div>
                <Label>{t('notes')}</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-company-btn">{t('cancel')}</Button>
              <Button onClick={handleCreate} className="cff-button-primary" data-testid="save-company-btn">{t('save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {companies.map(c => (
          <Card key={c.id} className="p-5 cff-hover-lift" data-testid={`company-card-${c.client_code}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start gap-2">
                <div className="bg-orange-50 p-2 rounded-md">
                  <Building2 className="h-4 w-4 text-[#556B2F]" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{c.company_name}</p>
                  <p className="text-xs text-gray-500 font-mono">{c.client_code} · {c.client_type}</p>
                </div>
              </div>
            </div>
            <div className="space-y-1 text-xs text-gray-600">
              <p>{c.contact_person} · {c.phone}</p>
              <p>{c.email}</p>
              <p className="inline-flex items-center gap-1 mt-1 flex-wrap">
                <span className="px-2 py-0.5 rounded bg-gray-100">{CLIENT_TYPE_LABELS[c.client_type] || c.client_type}</span>
                <span className="px-2 py-0.5 rounded bg-gray-100">{c.delivery_method === 'free_delivery' ? '🆓 Беспл.' : c.delivery_method === 'pickup' ? '🚗 Самовывоз' : '💰 Платная'}</span>
                {c.discount_percent > 0 && <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">-{c.discount_percent}%</span>}
              </p>
              <div className="pt-3 border-t border-gray-100 mt-3">
                <p className="text-xs font-medium text-gray-700 mb-2">
                  <MapPin className="h-3 w-3 inline" /> {locations[c.id]?.length || 0} locations
                </p>
                <Button variant="outline" size="sm" onClick={() => setLocationDialog(c)} data-testid={`add-location-${c.client_code}`}>
                  <Plus className="h-3 w-3 mr-1" /> Location
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Location Dialog */}
      <Dialog open={!!locationDialog} onOpenChange={(open) => !open && setLocationDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('add')} {t('deliveryLocation')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Location Name</Label>
              <Input value={locForm.location_name} onChange={(e) => setLocForm({...locForm, location_name: e.target.value})} data-testid="loc-name-input" />
            </div>
            <div>
              <Label>Recipient Name</Label>
              <Input value={locForm.recipient_name} onChange={(e) => setLocForm({...locForm, recipient_name: e.target.value})} />
            </div>
            <div>
              <Label>Recipient Phone</Label>
              <Input value={locForm.recipient_phone} onChange={(e) => setLocForm({...locForm, recipient_phone: e.target.value})} />
            </div>
            <div>
              <Label>{t('address')}</Label>
              <Textarea value={locForm.address} onChange={(e) => setLocForm({...locForm, address: e.target.value})} />
            </div>
            <div>
              <Label>Delivery Notes</Label>
              <Textarea value={locForm.delivery_notes} onChange={(e) => setLocForm({...locForm, delivery_notes: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLocationDialog(null)}>{t('cancel')}</Button>
            <Button onClick={handleCreateLocation} className="cff-button-primary" data-testid="save-location-btn">{t('save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
